/*
 * proUpgrade.js — Standard-edition "locked Pro feature" affordances.
 *
 * In the Standard edition we no longer *hide* Pro surfaces; we show them
 * disabled with a PRO crown badge so users discover what Pro adds. Clicking a
 * locked tab (or its in-panel button) opens a Standard-vs-Pro comparison dialog.
 *
 * The comparison mirrors slide 19 ("Two plans") of
 * docs/decks/afm-introduction.html — keep the two in sync.
 *
 * Exports:
 *   • makeProTab({ key, feature, icon, label })  → a Tabs `items` entry for a
 *     locked Pro tab (badged label + locked panel that opens the modal).
 *   • ProLockedCard({ feature, title })          → an inline locked card.
 *   • EditionComparisonModal({ open, onClose, highlight }) → the comparison dialog.
 *   • ProBadge                                    → the gold PRO crown tag.
 *   • PRO_FEATURE_META                            → per-feature copy.
 *
 * The runtime hasFeature() gate at each call site still decides Standard vs Pro;
 * this module only changes how the *Standard* branch looks.
 */
import React, { useEffect, useState } from 'react';
import { Modal, Tag, Space, Typography, Button, Card } from 'antd';
import {
  CrownOutlined,
  LockOutlined,
  CheckCircleFilled,
  MinusOutlined,
} from '@ant-design/icons';

const { Text, Paragraph } = Typography;

const GOLD = '#c9a961';
// Item 4: the crown icon carries no trailing margin of its own.
const CROWN = { marginInlineEnd: 0 };

// Per-Pro-feature copy, keyed by the capability keys hasFeature() uses.
export const PRO_FEATURE_META = {
  productivity: {
    name: 'Delivery ROI & productivity',
    blurb: 'Per-team velocity, delivery ROI and AI-cost-vs-output efficiency over your delivery data.',
  },
  savings: {
    name: 'Savings Finder',
    blurb: 'Seat-reclaim and model-mix recommendations that cut AI spend, with configurable rules.',
  },
  estimation: {
    name: 'Workload & cost estimation',
    blurb: 'Upcoming-work forecast plus per-task cycle-time, effort and cost estimation.',
  },
  forecasting: {
    name: 'Spend forecasting',
    blurb: 'Projected spend and budget burn-down so you can see overruns before they happen.',
  },
};

// The Standard-vs-Pro matrix, grouped exactly like deck slide 19. `std` is a
// boolean per row; Pro is ✓ for everything. `key` lets a caller highlight the
// row tied to the feature/source that was clicked.
const SECTIONS = [
  {
    title: 'Connected sources',
    rows: [
      { key: 'github-copilot', name: 'GitHub Copilot', std: true },
      { key: 'claude-code', name: 'Claude (Anthropic)', std: true },
      { key: 'm365', name: 'Microsoft 365 Copilot', std: false },
      { key: 'cursor', name: 'Cursor', std: false },
      { key: 'codex', name: 'OpenAI API (Codex and other API usage)', std: false },
      { key: 'bedrock', name: 'AWS Bedrock', std: false },
      { key: 'azure-foundry', name: 'Azure AI Foundry', std: false },
      { key: 'jira-rally', name: 'Jira / Rally & other sources', std: false },
    ],
  },
  {
    title: 'Features',
    rows: [
      { key: 'usage', name: 'AI usage', std: true },
      { key: 'adoption', name: 'AI adoption', std: true },
      { key: 'billing', name: 'AI billing', std: true },
      { key: 'budget', name: 'Budget management', std: true },
      { key: 'productivity', name: 'Delivery ROI & productivity', std: false },
      { key: 'forecasting', name: 'Spend forecasting', std: false },
      { key: 'estimation', name: 'Workload & cost estimation', std: false },
      { key: 'savings', name: 'Savings Finder', std: false },
      { key: 'cost-efficiency', name: 'Cost efficiency', std: false },
    ],
  },
];

const Yes = () => <CheckCircleFilled style={{ color: '#16a34a', fontSize: 15 }} />;
const No = () => <MinusOutlined style={{ color: '#cbd5e1', fontSize: 14 }} />;

const cellTd = { padding: '6px 10px', textAlign: 'center', borderBottom: '1px solid #f0f0f0', width: 110 };
const featTd = { padding: '6px 10px', borderBottom: '1px solid #f0f0f0' };

export function EditionComparisonModal({ open, onClose, highlight = null }) {
  // These dialogs are frequently rendered inside a clickable card. AntD portals
  // the Modal to <body>, but React events still bubble through the *React* tree,
  // so a click on the mask / close / OK would re-fire the card's onClick (which
  // re-opens the modal). Swallow click propagation at the wrapper to prevent it.
  return (
    <span onClick={(e) => e.stopPropagation()}>
    <Modal
      open={open}
      onCancel={onClose}
      onOk={onClose}
      okText="Got it"
      cancelButtonProps={{ style: { display: 'none' } }}
      width={640}
      title={<Space><CrownOutlined style={{ ...CROWN, color: GOLD }} /> Two plans — Standard vs Pro</Space>}
    >
      <Paragraph type="secondary" style={{ marginBottom: 14 }}>
        Your deployment is on the <Text strong>Standard</Text> edition — Copilot &amp;
        Claude with usage, adoption, billing and budgets. <Text strong>Pro</Text> adds
        every other source plus delivery ROI, forecasting, estimation and the Savings
        Finder that typically pays for the platform.
      </Paragraph>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr>
            <th style={{ ...featTd, textAlign: 'left', borderBottom: '2px solid #1f2937' }} />
            <th style={{ ...cellTd, borderBottom: '2px solid #1f2937' }}>
              <div style={{ fontWeight: 700 }}>Standard</div>
              <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 400 }}>Copilot &amp; Claude</div>
            </th>
            <th style={{ ...cellTd, width: 150, borderBottom: `2px solid ${GOLD}`, background: '#fbf7ec' }}>
              <div style={{ fontWeight: 800, color: GOLD }}><CrownOutlined style={CROWN} /> Pro</div>
              <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 400 }}>All sources · full platform</div>
            </th>
          </tr>
        </thead>
        <tbody>
          {SECTIONS.map((section) => (
            <React.Fragment key={section.title}>
              <tr>
                <td colSpan={3} style={{
                  padding: '8px 10px 4px', fontSize: 11, fontWeight: 800,
                  letterSpacing: '.05em', textTransform: 'uppercase', color: '#94a3b8',
                }}>
                  {section.title}
                </td>
              </tr>
              {section.rows.map((r) => {
                const hl = r.key === highlight;
                return (
                  <tr key={r.key} style={hl ? { background: '#f7f1e1' } : undefined}>
                    <td style={{ ...featTd, fontWeight: hl ? 700 : 400 }}>{r.name}</td>
                    <td style={cellTd}>{r.std ? <Yes /> : <No />}</td>
                    <td style={{ ...cellTd, width: 150, background: hl ? '#f3ead2' : '#fbf7ec' }}><Yes /></td>
                  </tr>
                );
              })}
            </React.Fragment>
          ))}
        </tbody>
      </table>
      <Paragraph type="secondary" style={{ marginTop: 14, marginBottom: 0, fontSize: 12 }}>
        Pro is enabled with a Pro license — contact your administrator to upgrade.
      </Paragraph>
    </Modal>
    </span>
  );
}

// The gold PRO crown tag. Crown carries no right margin (item 4); the gap before
// "PRO" comes from the tag's own padding.
export function ProBadge({ style }) {
  return (
    <Tag color="gold" style={{ marginInlineEnd: 0, ...style }}>
      <CrownOutlined style={CROWN} /> PRO
    </Tag>
  );
}

// Badged, dimmed tab label so the feature reads as "Pro, not available here".
function ProTabLabel({ icon, children }) {
  return (
    <Space size={6} style={{ opacity: 0.65 }}>
      {icon}
      <span>{children}</span>
      <ProBadge style={{ fontSize: 10, lineHeight: '16px', padding: '0 6px' }} />
    </Space>
  );
}

// Tab content for a locked Pro feature: a centered locked teaser that opens the
// comparison dialog when the tab is first activated, with a button to reopen it.
function ProLockedPanel({ feature }) {
  const meta = PRO_FEATURE_META[feature] || { name: 'This feature', blurb: '' };
  const [open, setOpen] = useState(false);

  // Auto-open the dialog when the user clicks into the locked tab.
  useEffect(() => {
    setOpen(true);
  }, []);

  return (
    <div style={{ padding: '40px 16px', textAlign: 'center' }}>
      <LockOutlined style={{ fontSize: 34, color: GOLD }} />
      <div style={{ fontSize: 17, fontWeight: 700, marginTop: 12 }}>
        {meta.name} <ProBadge style={{ marginInlineStart: 6 }} />
      </div>
      <Paragraph type="secondary" style={{ maxWidth: 460, margin: '8px auto 16px' }}>
        {meta.blurb} Available in the <Text strong>Pro</Text> edition.
      </Paragraph>
      <Button type="primary" icon={<CrownOutlined style={CROWN} />} onClick={() => setOpen(true)}>
        Compare Standard &amp; Pro
      </Button>
      <EditionComparisonModal open={open} onClose={() => setOpen(false)} highlight={feature} />
    </div>
  );
}

// Inline locked card — for Pro surfaces that aren't a whole tab (e.g. the
// IndividualView "Upcoming tasks & workload" estimation card).
export function ProLockedCard({ feature, title }) {
  const meta = PRO_FEATURE_META[feature] || { name: title, blurb: '' };
  const [open, setOpen] = useState(false);
  return (
    <Card
      style={{ marginTop: 16 }}
      title={<Space>{title || meta.name}<ProBadge /></Space>}
    >
      <div style={{ textAlign: 'center', padding: '20px 8px' }}>
        <LockOutlined style={{ fontSize: 26, color: GOLD }} />
        <Paragraph type="secondary" style={{ maxWidth: 420, margin: '10px auto 14px' }}>
          {meta.blurb} Available in the <Text strong>Pro</Text> edition.
        </Paragraph>
        <Button icon={<CrownOutlined style={CROWN} />} onClick={() => setOpen(true)}>
          Compare Standard &amp; Pro
        </Button>
      </div>
      <EditionComparisonModal open={open} onClose={() => setOpen(false)} highlight={feature} />
    </Card>
  );
}

// Locked KPI tile — sized to sit among real metric tiles (e.g. the forecast
// tiles in BillingKpiRow). Clicking opens the comparison dialog.
export function ProLockedMetricCard({ title, feature }) {
  const [open, setOpen] = useState(false);
  return (
    <Card
      hoverable
      onClick={() => setOpen(true)}
      styles={{ body: { padding: '8px 12px' } }}
      style={{ height: '100%', opacity: 0.85, cursor: 'pointer' }}
    >
      <div style={{ fontSize: 12, color: '#8c8c8c', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        {title} <ProBadge style={{ fontSize: 9, lineHeight: '15px', padding: '0 5px' }} />
      </div>
      <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
        <LockOutlined style={{ color: GOLD }} />
        <span style={{ fontSize: 12, color: '#94a3b8' }}>Available in Pro</span>
      </div>
      <EditionComparisonModal open={open} onClose={() => setOpen(false)} highlight={feature} />
    </Card>
  );
}

// Build a Tabs `items` entry for a locked Pro tab.
export function makeProTab({ key, feature, icon, label }) {
  return {
    key,
    label: <ProTabLabel icon={icon}>{label}</ProTabLabel>,
    children: <ProLockedPanel feature={feature} />,
  };
}
